import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { ComponentProps } from '../../../..';
import useFireTrackingEvent from '../../../../../../hooks/useFireTrackingEvent';
import { iframeSizes } from '../../../../../../shared/constants';
import { Appearance, FrameSizeSelector } from '../../../../../../shared/types';
import Icon from '../../../../../Icon';
import { Heading2 } from '../../../../../Typography';

import useLocale from '../../../../../../hooks/useLocale';
import useWidgetContext from '../../../../../../hooks/useWidgetContext';
import { isNil } from 'ramda';
import usePathPrefix from '../../../../../../hooks/usePathPrefix';
import cleanPath from '../../../../../../utils/cleanPath';

import useStateContext from '../../../../../../hooks/useStateContext';
import ListItem from './components/ListItem';
import arrayToCss from '../../../../../../utils/arrayToCss';
import CloseButton, {
  CLOSE_BUTTON_SIZE,
} from '../../../Large/Variants/MultiApps/components/CloseButton';
import Footer from './components/Footer';
import { ROOT } from '../../../../../../constants/paths';
import take3 from '../../../../../../utils/take3';
import getPinnedApps from '../../../../../../utils/getPinnedApps';
import useFrameSizeSelector from '../../../../../../hooks/useFrameSizeSelector';

const text = {
  header: {
    nl: 'Aanvullende diensten',
    en: 'Complementary services',
  },
};

const MultiApps: React.FC<Omit<ComponentProps, 'size'>> = ({
  dataTestId,
  onDismiss,
  onTrigger,
  ...rest
}) => {
  const widgetContext = useWidgetContext();
  const pathPrefix = usePathPrefix();
  const locale = useLocale();
  const currentBp = useFrameSizeSelector();
  const fireEvent = useFireTrackingEvent();
  const { appsOpened } = useStateContext();
  const [activeSlide, setActiveSlide] = useState<string>('slide-1');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const pinnedApps = getPinnedApps(Object.values(widgetContext.apps));

  const top3 =
    pinnedApps.length !== 0
      ? take3(pinnedApps)
      : take3(Object.values(widgetContext.apps));

  const amountOfOther = Object.keys(widgetContext.apps).length - top3.length;

  useEffect(() => {
    if (listRef.current !== null) {
      const options = {
        root: listRef.current,
        rootMargin: '0px',
        threshold: 0.8,
      };

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSlide(entry.target.id);
          }
        });
      }, options);

      const listItems = document.querySelectorAll('.slide');

      listItems.forEach(el => {
        observer.observe(el);
      });
    }
  }, []);

  const onNext = () => {
    if (listRef.current) {
      const activeSlideN = parseInt(activeSlide.split('-')[1]);
      const nextSlideN = activeSlideN <= top3.length - 1 ? activeSlideN + 1 : 1;
      const nextSlideEl = document.getElementById(`slide-${nextSlideN}`);

      if (nextSlideEl) {
        listRef.current.scrollLeft = nextSlideEl.offsetLeft - 20;
      }
    }
  };

  const onPrevious = () => {
    if (listRef.current) {
      const activeSlideN = parseInt(activeSlide.split('-')[1]);
      const previousSlideN = activeSlideN <= 1 ? 3 : activeSlideN - 1;

      const previousSlideEl = document.getElementById(
        `slide-${previousSlideN}`,
      );

      if (previousSlideEl) {
        listRef.current.scrollLeft = previousSlideEl.offsetLeft - 20;
      }
    }
  };

  return (
    <Container
      ref={containerRef}
      {...rest}
      data-testid={dataTestId}
      $currentBp={currentBp}
      $size={Appearance.Large}
      $moreThanThree={top3.length >= 3}
    >
      <Header>
        <Heading2 withoutMargin>{text.header[locale]}</Heading2>
        <CloseButton
          onClick={e => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          <Icon name="close" />
        </CloseButton>
      </Header>
      <ShadowContainer>
        {activeSlide !== 'slide-1' && (
          <PreviousButton onClick={onPrevious}>
            <ButtonLabel>Vorige app</ButtonLabel>
            <Icon name="chevron-left" />
          </PreviousButton>
        )}
        <TileList ref={listRef}>
          {top3.map((app, index) => {
            // When we disable apps from CROCS not having this check breaks the preview.
            if (isNil(app)) return null;

            const { title, slug, icon } = app;
            const path = `${pathPrefix}/apps/${slug}`;
            return (
              <Li
                key={app.slug}
                id={`slide-${index + 1}`}
                className="slide"
                tabIndex={-1}
                $preventAnimation={
                  top3.length === 1 || activeSlide !== 'slide-1'
                }
              >
                <ListItem
                  dataTestId={`multi-app-trigger-item-${app.slug}`}
                  title={title[locale]}
                  icon={icon}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const cleanedPath = cleanPath(path);
                    appsOpened.setValue(prev => {
                      if (!prev.includes(cleanedPath)) {
                        return [...prev, cleanedPath];
                      }
                      return prev;
                    });
                    onTrigger(cleanedPath);
                  }}
                />
              </Li>
            );
          })}
        </TileList>
        {top3.length !== 1 && (
          <NextButton onClick={onNext}>
            <ButtonLabel>Volgende app</ButtonLabel>
            <Icon name="chevron-right" />
          </NextButton>
        )}
      </ShadowContainer>
      <Footer
        amountOfOther={amountOfOther}
        activeSlide={activeSlide}
        slides={top3}
        onClick={() => {
          onTrigger(ROOT);
          fireEvent({
            path: ROOT,
            event: 'ExpandWidget',
            triggerType: 'footerClick',
          });
        }}
      />
    </Container>
  );
};

const Container = styled.div<{
  $currentBp: FrameSizeSelector;
  $size: Appearance;
  $moreThanThree?: boolean;
}>(
  ({ theme, $currentBp, $size }) => css`
    position: relative;
    border-radius: ${theme.getSystem().border.radius.base};
    width: ${iframeSizes.collapsedMulti[$size][$currentBp].width};
    max-width: calc(
      ${iframeSizes.collapsedMulti[$size][$currentBp].width} -
        ${CLOSE_BUTTON_SIZE}px
    );
    box-shadow: ${theme.boxShadow('s')};
    background-color: ${theme.color('primary')};
    margin: ${$currentBp === 'mobile' ? '0 auto' : 0};

    display: flex;
    flex-direction: column;

    overflow-x: hidden;

    &::-webkit-scrollbar {
      display: none;
    }

    h3 {
      color: ${theme.color('primary', 'text')};
    }
  `,
);

const Header = styled.header(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${arrayToCss(['base', 'base', null, 'base'], theme)};

    h2 {
      color: ${theme.color('primary', 'text')};
    }
  `,
);

const ButtonLabel = styled.span`
  display: inline-block;
  text-indent: -9999px;
  white-space: nowrap;
  width: 0;
  visibility: hidden;
  display: none;
`;

const NavButtonBase = styled.button(
  ({ theme }) => css`
    position: absolute;
    top: calc(50% - 1rem);
    z-index: 50;
    padding: 0.55rem;
    margin: 0;

    background-color: ${theme.color('primary', 'text')};
    color: ${theme.color('primary')};
    border: 1px solid ${theme.color('primary')};
    border-radius: ${theme.getSystem().border.radius.full};

    cursor: pointer;

    height: 2rem;
    width: 2rem;

    transition: all 0.2s ease-out;

    &:hover {
      outline: 2px solid ${theme.color('secondary')};
    }

    svg path {
      stroke-width: 4px;
    }
  `,
);

const PreviousButton = styled(NavButtonBase)(
  ({ theme }) => css`
    left: ${theme.space('xxs')};
  `,
);

const NextButton = styled(NavButtonBase)(
  ({ theme }) => css`
    right: ${theme.space('xxs')};
  `,
);

const ShadowContainer = styled.div(
  ({ theme }) => css`
    position: relative;

    &:before,
    &:after {
      z-index: 99;
      content: ' ';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 0.5rem;
      opacity: 0.35;
    }

    &:before {
      left: 0;
      background: linear-gradient(
        90deg,
        ${theme.color('primary')} 0%,
        transparent 100%
      );
    }

    &:after {
      right: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        ${theme.color('primary')} 100%
      );
    }
  `,
);

const bounce = keyframes`
  80% { transform: translate(0px, 0px); }
  85% { transform: translate(-7px, 0px); }
  90% { transform: translate(0px, 0px); }
  100% { transform: translate(0px, 0px); }
`;

const TileList = styled.ul(
  ({ theme }) => css`
    position: relative;
    list-style: none;
    padding: ${theme.space('xs')} 0;
    margin: 0;
    transition: all 0.2s ease-out;
    -webkit-overflow-scrolling: touch;

    scroll-snap-type: x mandatory;

    display: flex;
    overflow-x: scroll;
    scroll-behavior: smooth;

    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  `,
);

const Li = styled.li<{ $preventAnimation?: boolean }>(
  ({ $preventAnimation = false, theme }) => css`
    flex-shrink: 0;
    flex: 1 0 90%;
    width: 90%;
    height: 100%;
    scroll-snap-align: start;
    scroll-snap-stop: always;

    animation: ${$preventAnimation ? 'none' : bounce} 5s ease-out infinite 3s;

    &:only-child {
      width: 100%;
    }

    padding-left: ${theme.space('xs')};

    &:last-child {
      padding-right: ${theme.space('xs')};
    }
  `,
);

export default MultiApps;
